import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	computed, inject,
	signal,
	TemplateRef,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import {isNil, isUndefined} from "lodash";
import moment from "moment";
import {BannerService} from "src/app/core/services/services/banner.service";
import {PreferencesService} from "src/app/core/services/services/preferences.service";
import {Banner, IBTemplateData} from "src/app/core/components/information-banner/information-banner-templates";
import {TranslateService} from "@ngx-translate/core";
import {TranslatableComponent} from "src/app/core/models/TranslatableComponent";
import {CookieBannerTemplate} from "src/app/core/components/information-banner/banners/cookie-banner";
import {CustomizingService} from "../../services/services/customizing.service";
import {MessageBannerTemplate} from "./banners/simple-message-banner";
import {DomSanitizer} from "@angular/platform-browser";
import {AppearanceService} from "../../services/services/appearance.service";
import {LanguageEnum} from "../../models/Enums/LanguageEnum";
import {NgClass, NgTemplateOutlet} from "@angular/common";
import {MatIcon} from "@angular/material/icon";

@Component({
	selector: "app-information-banner",
	templateUrl: "./information-banner.component.html",
	styleUrls: ["./information-banner.component.scss"],
	imports: [NgClass, NgTemplateOutlet, MatIcon],
})
export class InformationBannerComponent extends TranslatableComponent implements AfterViewInit {
	/**
	 * if you want to add more templates, you need to add them here
	 */
	@ViewChild("cookieBanner", {read: TemplateRef})
	private readonly cookieBannerTemplate: TemplateRef<IBTemplateData>;
	@ViewChild("msgBanner", {read: TemplateRef})
	private readonly messageBannerTemplate: TemplateRef<IBTemplateData>;

	private readonly preferenceService = inject(PreferencesService);
	private readonly customizingService = inject(CustomizingService);
	private readonly bannerService = inject(BannerService);
	private readonly sanitizer = inject(DomSanitizer);
	private readonly appearanceService = inject(AppearanceService);

	/**
	 * banners that should/could be shown initially
	 */
	banners = signal(<Banner[]>[
		{
			template: new CookieBannerTemplate(this.preferenceService),
		},
	]);

	shownBanners = computed(() => {
		return this.banners().filter((value) => {
			return (
				(isUndefined(value.shownTill) || moment(value.shownTill).diff(moment.now(), "days") >= 0) &&
				(!value.hidden || isUndefined(value.hidden))
			);
		});
	});

	currentShownBanner = computed(() => {
		return this.shownBanners()[0];
	});

	hidden = signal(true);

	private readonly cdr = inject(ChangeDetectorRef);
	private readonly _translate = inject(TranslateService);

	constructor() {
		super(inject(TranslateService), "InformationBannerComponent");

		this.registerBannerAddAPI(this.bannerService);
	}

	async ngAfterViewInit() {
		this.cdr.detectChanges();

		await this.computeBannerHiddenStatus();
		await this.createBannersFromCustomizing();

		if (this.shownBanners().length > 0) {
			setTimeout(() => {
				this.hidden.set(false);
			}, 1e3);
		}
	}

	//#region template helpers
	hasCurrentTemplate() {
		return this.shownBanners().length > 0 && !isNil(this.getCurrentTemplate());
	}

	getCurrentTemplate() {
		return this.currentShownBanner().template.templateRef === "cookieBanner"
			? this.cookieBannerTemplate
			: this.currentShownBanner().template.templateRef === "msgBanner"
				? this.messageBannerTemplate
				: undefined;
	}

	//#endregion

	//#region general helpers

	async hideBanner(bannerToDelete: Banner = this.currentShownBanner()) {
		if (!this.hidden() && this.shownBanners().length === 1 && this.shownBanners().includes(bannerToDelete)) {
			this.hidden.set(true);
			await new Promise<void>((resolve) => {
				setTimeout(() => {
					resolve();
				}, 1e3);
			});
		}

		this.banners.update((value) => {
			let indexOf = value.indexOf(bannerToDelete);
			if (indexOf === -1) {
				return value;
			}
			value[indexOf].hidden = true;
			// Return a NEW array reference so the signal notifies and the `shownBanners` computed
			// recomputes. Mutating and returning the same reference is an Object.is no-op → no update.
			return [...value];
		});
	}

	private registerBannerAddAPI(bannerService: BannerService) {
		bannerService.bannerCreationTrigger.subscribe(async (banner) => {
			if (banner.template.shouldBeHidden) {
				banner.hidden = await banner.template.shouldBeHidden();
			}

			this.banners.update((banners) => {
				return [banner, ...banners];
			});
			if (this.shownBanners().length > 0 && this.hidden()) {
				setTimeout(() => {
					this.hidden.set(false);
				});
			}
		});
		bannerService.bannerDeletionTrigger.subscribe((banner) => {
			this.hideBanner(banner).then();
		});
	}

	private async computeBannerHiddenStatus() {
		let updateStatements: Promise<void>[] = [];
		for (let banner of this.banners()) {
			if (!isUndefined(banner.template.shouldBeHidden)) {
				updateStatements.push(
					new Promise((resolve) => {
						banner.template.shouldBeHidden().then((hidden) => {
							this.banners.update((value) => {
								value[value.indexOf(banner)].hidden = hidden;
								// New array reference → the signal notifies and `shownBanners` recomputes.
								// Without this, the cookie banner (set hidden=true once consent is stored)
								// stays in the stale computed and re-appears on every page reload.
								return [...value];
							});
							resolve();
						});
					}),
				);
			}
		}
		await Promise.all(updateStatements);
	}

	private async createBannersFromCustomizing() {
		let banners = await this.customizingService.getBanners();
		banners.reverse();
		let language = this.appearanceService.getLanguage();

		banners.forEach((banner) => {
			this.bannerService.createBanner({
				height: banner.height,
				template: new MessageBannerTemplate(
					{
						msg: this.sanitizer.bypassSecurityTrustHtml(
							LanguageEnum.DE.toString() === language ? banner.messageDE : banner.messageEN,
						),
						type: banner.type,
						showOnlyIfNotDismissedOnceByUserKey: banner.messageDE + banner.messageEN,
						dismissible: banner.persistence === 1,
					},
					this.preferenceService,
				),
			});
		});
	}

	//#endregion
	protected readonly isNil = isNil;
}
